'use client';
import {
  Button,
  CardPricing,
  type ImageAspectRatio,
  ProductCardImage,
  Typography,
} from '@curology/ui-components-web';
import { CardRating } from '@curology/ui-components-web/react/Card/CardRating';
import LockIcon from '@curology/ui-components-web/react/Icon/icons/LockIcon';
import MinusIcon from '@curology/ui-components-web/react/Icon/icons/MinusIcon';
import PlusIcon from '@curology/ui-components-web/react/Icon/icons/PlusIcon';
import { convertSchemaToHtml } from '@thebeyondgroup/shopify-rich-text-renderer';
import clsx from 'clsx';
import Image from 'next/image';
import { useEffect, useMemo, useState } from 'react';
import Slider from 'react-slick';

import { useVisitContext } from '@/services/visitsService/hooks/useVisitContext';
import { useCart } from '@/utils/shopify/hooks/useCart';
import { useProduct } from '@/utils/shopify/hooks/useProduct';
import { useProductVariant } from '@/utils/shopify/hooks/useProductVariant';
import {
  findSellingPlanAllocationByFrequency,
  orderGrooveTrialAttribute,
} from '@/utils/shopify/utils/sellingPlans';

import { ProductModal } from './productModal';
import { type ShopifyMetafieldImages, type ShopifyCartProduct } from '../types';
import { useShopifyProduct } from '../useShopifyProduct';
import '../css/carousel.css';
import { formatMoneyV2 } from '../utils/money';

const ProductHeroCarousel = ({
  product,
  aspectRatio,
}: {
  product: ShopifyCartProduct;
  aspectRatio: ImageAspectRatio;
}) => {
  const { metafield } = useShopifyProduct({ product });

  const images = useMemo(() => {
    const data = metafield({
      key: 'recommendation_carousel',
    }) as unknown as ShopifyMetafieldImages;

    return data?.references?.nodes.map((node) => node.image) || [];
  }, [metafield]);

  return (
    <div className="product-card-image-carousel-container">
      <Slider
        accessibility={true}
        arrows={false}
        dots={true}
        draggable={true}
        infinite={true}
      >
        {images.map((image) => {
          return (
            <ProductCardImage
              alt={image.altText || product.title || 'Product'}
              aspectRatio={aspectRatio}
              className="cur-cursor-default"
              key={image.id}
              onClick={(event) => event.stopPropagation()}
              src={image.url}
            />
          );
        })}
      </Slider>
    </div>
  );
};

const ProductIngredients = ({ product }: { product: ShopifyCartProduct }) => {
  const { activeIngredients, isRx } = useShopifyProduct({ product });

  const [selectedIngredient, setSelectedIngredient] = useState(
    activeIngredients.length > 0 ? activeIngredients[0] : null,
  );

  const activeIngredientsWithThumbnails = useMemo(
    () =>
      activeIngredients.filter(
        (ingredient) => ingredient.thumbnail && ingredient.thumbnail.url,
      ),
    [activeIngredients],
  );

  if (!isRx) {
    return;
  }

  return (
    <div>
      <ul className="cur-flex cur-mb-0 cur-overflow-x-auto">
        {activeIngredientsWithThumbnails.map((ingredient, index) => (
          <li className="cur-shrink-0" key={ingredient.title}>
            <div
              className={clsx(
                'cur-p-4 cur-border-b-2',
                ingredient.title === selectedIngredient?.title
                  ? 'cur-border-border-secondary'
                  : 'cur-border-transparent',
              )}
              onClick={() => setSelectedIngredient(ingredient)}
            >
              <Image
                alt={ingredient.thumbnail.altText || 'ingredient'}
                height="40"
                src={ingredient.thumbnail.url}
                width="40"
              />
            </div>
          </li>
        ))}
      </ul>
      {selectedIngredient && (
        <div className="cur-pt-4">
          <Typography className="cur-mb-2" weight="bold">
            {selectedIngredient.title}
          </Typography>
          <Typography className="cur-my-2">
            {selectedIngredient.short_title}
          </Typography>
          <Typography
            className="cur-text-surface-secondary cur-mt-2"
            size="meta"
          >
            Ingredients subject to consultation.
          </Typography>
        </div>
      )}
    </div>
  );
};

const ProductCard = ({
  productId,
  productVariantId,
  aspectRatio,
  required = false,
}: {
  productId: string;
  productVariantId: string;
  aspectRatio: ImageAspectRatio;
  required?: boolean;
}) => {
  const { metafield, shortDescription, reviews, recommendationChip, product } =
    useProduct(productId);
  const { productVariant } = useProductVariant(productVariantId);
  const [processing, setProcessing] = useState(false);
  const [salePrice, setSalePrice] = useState<string | undefined>(undefined);

  const [modal, setModal] = useState(false);
  const { visit } = useVisitContext();
  const { isProductVariantSelected, addItem, removeItem, getLineItem, cart } =
    useCart({
      onChangeStart: () => setProcessing(true),
      onChangeFinish: () => setProcessing(false),
    });
  const lineItem = getLineItem(productVariantId);
  const sellingPlanAllocation = findSellingPlanAllocationByFrequency({
    sellingPlanAllocations: productVariant.sellingPlanAllocations.nodes,
    frequency: '60 days',
  });

  useEffect(() => {
    const isCartFreeTrialDiscountApplicable = !!cart?.discountCodes.find(
      (discount) => discount.code === 'FREETRIAL',
    )?.applicable;

    if (lineItem?.cost.totalAmount) {
      setSalePrice(formatMoneyV2({ amount: lineItem.cost.totalAmount }));
    } else if (!lineItem && isCartFreeTrialDiscountApplicable) {
      setSalePrice(
        formatMoneyV2({
          amount: {
            amount: '0',
            currencyCode: productVariant.price.currencyCode,
          },
        }),
      );
    }
  }, [lineItem]);

  if (!product) {
    return null;
  }

  return (
    <div className="cur-flex cur-flex-col cur-bg-white">
      <div className="cur-relative cur-p-1">
        <div className="cur-relative">
          {recommendationChip && (
            <div className="cur-absolute cur-z-20 cur-top-0 cur-left-0">
              <span className="cur-inline-flex cur-items-center cur-bg-surface-brand-accent cur-px-2 cur-py-1 cur-text-meta cur-font-bold text-gray-600">
                {recommendationChip}
              </span>
            </div>
          )}
          <ProductHeroCarousel aspectRatio={aspectRatio} product={product} />
        </div>
      </div>
      <div className="cur-py-4 cur-px-4">
        <div className="cur-cursor-pointer" onClick={() => setModal(true)}>
          <div className="cur-flex cur-justify-between cur-items-start">
            <div>
              <Typography
                size="h3"
                dangerouslySetInnerHTML={{
                  __html:
                    metafield({ key: 'display_title' })?.value || product.title,
                }}
              />
              <CardPricing
                className="cur-mr-1"
                price={formatMoneyV2({
                  amount: lineItem?.cost.subtotalAmount ?? productVariant.price,
                })}
                salePrice={salePrice}
              />
            </div>
            {required && (
              <div
                className={clsx(
                  'cur-flex cur-items-center cur-text-curology-brand-600',
                  {
                    curology: 'cur-text-curology-brand-600',
                    agency: 'cur-text-agency-neutral-900',
                  }[visit?.brand || 'curology'],
                )}
              >
                <LockIcon className="cur-w-4 cur-h-4" />
                <Typography size="meta" className="cur-ml-1">
                  Required
                </Typography>
              </div>
            )}
          </div>
          {shortDescription && (
            <Typography
              className="cur-mt-2"
              size="body"
              dangerouslySetInnerHTML={{
                __html: convertSchemaToHtml(shortDescription),
              }}
            />
          )}
        </div>
        <ProductIngredients product={product} />
        {reviews && (
          <CardRating
            count={reviews.count}
            showCount={false}
            size="sm"
            value={reviews.average}
            readOnly
          />
        )}
      </div>

      {!required && (
        <div className="cur-mt-auto cur-p-4 cur-flex">
          {isProductVariantSelected(productVariantId) ? (
            <Button
              buttonType="secondary"
              onClick={() => removeItem(productVariantId)}
              className="cur-w-full"
              loading={processing}
              disabled={processing}
            >
              <MinusIcon className="cur-w-4 cur-h-4 cur-mr-2" /> Remove
            </Button>
          ) : (
            <Button
              onClick={() =>
                addItem({
                  productVariantId,
                  sellingPlanId: sellingPlanAllocation?.sellingPlan.id,
                  attributes: [orderGrooveTrialAttribute],
                })
              }
              size="md"
              className="cur-w-full"
              loading={processing}
              disabled={processing}
            >
              <PlusIcon className="cur-w-4 cur-h-4 cur-mr-2" /> Add to box
            </Button>
          )}
        </div>
      )}

      <ProductModal
        open={modal}
        onClose={() => setModal(false)}
        product={product}
      />
    </div>
  );
};

export { ProductCard };
